import { useFocusEffect } from "@react-navigation/native";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "native-base";
import React, { useMemo, useState } from "react";

import Toast from "@/components/atoms/Toast";
import AlertModal from "@/components/molecules/AlertModal";
import { useViewport } from "@/contexts/ViewportContext";
import { useAppNavigation } from "@/hooks/application/navigation";
import { useAppDispatch, useAppSelector } from "@/hooks/application/store";
import { useShipmentValue } from "@/hooks/domain/shipmentValue";
import { Order, OrderVoucherDetails } from "@/interfaces/domain/Order";
import { VoucherDiscountTypesEnum } from "@/interfaces/domain/Voucher";
import { RootNavigation } from "@/navigation/Root";
import analytics from "@/services/analytics";
import { CreateOrderPayload, createOrder } from "@/services/api/order";
import { logger } from "@/services/logger";
import { RESET_STORE_AND_ITENS, SET_SHIPMENT_VALUE } from "@/store/ducks/checkout";

import CheckoutMobile from "./Checkout.mobile";
import CheckoutWeb from "./Checkout.web";
import ClientDocumentModal from "./components/ClientDocumentModal";
import FallbackContactModal from "./components/FallbackContactModal";
import OrderCompletedModal from "./components/OrderCompletedModal";
import OutdatedPromoPricesModal from "./components/OutdatedPromoPricesModal";
import PaymentMethodModal from "./components/PaymentMethodModal";
import ScheduleModal from "./components/ScheduleModal";
import VoucherModal from "./components/VoucherModal";
import { checkItemsOutdateInPromo } from "./functions";
import {
  DeliveryTimeResult,
  FallbackContact,
  ModalTypesEnum,
  PaymentMethod,
  VoucherDatails,
} from "./types";

const Checkout = () => {
  const { isWebMode } = useViewport();
  const { goBack, navigate, isFocused, navigateBetweenStacks } = useAppNavigation<RootNavigation>();
  const dispatch = useAppDispatch();
  const toast = useToast();

  const { user } = useAppSelector((store) => store.auth);
  const { items, shipmentValue, store, subtotal, address, itemsNotFound } = useAppSelector(
    (store) => store.checkout
  );

  const [modalScheduleVisible, setModalScheduleVisible] = useState(false);
  const [modalShipmentErrorVisible, setModalShipmentErrorVisible] = useState(false);
  const [modalOutdatedPromoPriceVisible, setModalOutdatedPromoPriceVisible] = useState(false);
  const [modalPaymentMethodVisible, setModalPaymentMethodVisible] = useState(false);
  const [modalClientDocumentVisible, setModalClientDocumentVisible] = useState(false);
  const [modalFallbackContactVisible, setModalFallbackContactVisible] = useState(false);
  const [modalOrderSuccessVisible, setModalOrderSuccessVisible] = useState(false);
  const [modalVoucherVisible, setModalVoucherVisible] = useState(false);

  const [deliveryTimeResult, setDeliveryTimeResult] = useState<DeliveryTimeResult>(null);
  const [currentSubtotal, setCurrenSubtotal] = useState(subtotal);
  const [currentShipmentValue, setCurrenShipmentValue] = useState(shipmentValue);
  const [outdatedPromoPricesInfo, setOutdatedPromoPricesInfo] = useState<{
    isOutdated: boolean;
    newSubtotal: number;
  }>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod>(null);
  const [selectedClientDocument, setSelectedClientDocument] = useState("");
  const [selectedFallbackContact, setSelectedFallbackContact] = useState<FallbackContact>(null);
  const [selectedVoucher, setSelectedVoucher] = useState<VoucherDatails>();
  const [voucherOrderDiscount, setVoucherOrderDiscount] = useState(0);

  const serviceTaxValue = useMemo<number | null>(() => {
    if (!store?.porcentagem_taxa_servico) return null;
    return Number(store.porcentagem_taxa_servico) * currentSubtotal;
  }, [currentSubtotal, store]);

  const total = useMemo(() => {
    return currentSubtotal + (serviceTaxValue ?? 0) + currentShipmentValue - voucherOrderDiscount;
  }, [currentSubtotal, serviceTaxValue, currentShipmentValue, voucherOrderDiscount]);

  const clientId = user?.cliente?._id;

  const { data: shipmentData, isError: shipmentError } = useShipmentValue(store?._id, {
    enabled: !!store?._id,
    onError: () => {
      dispatch(SET_SHIPMENT_VALUE(null));
      setModalShipmentErrorVisible(true);
    },
  });

  const { mutate: mutateNewOrder, isLoading } = useMutation<Order, unknown, CreateOrderPayload>({
    mutationFn: (payload) => createOrder(payload),
    onSuccess: (data) => {
      analytics.logFacebookPurchase(data?.valor_total);
      analytics.logFirebasePurchase(data?.valor_total);
      setDeliveryTimeResult(null);
      setOutdatedPromoPricesInfo(null);
      setSelectedClientDocument(null);
      setSelectedFallbackContact(null);
      setSelectedPaymentMethod(null);
      setModalOrderSuccessVisible(true);
      dispatch(RESET_STORE_AND_ITENS());
      setTimeout(() => {
        setModalOrderSuccessVisible(false);
        navigateBetweenStacks(
          "orderStack",
          "orderTracking",
          {
            orderId: data?._id,
          },
          true
        );
      }, 1000);
    },
    onError: (error) => {
      logger.error(error);
      toast.show({
        render: () => (
          <Toast
            variant="error"
            message="Erro ao processar seu pedido, tente novamente mais tarde!"
          />
        ),
      });
    },
  });

  useFocusEffect(() => {
    const value = shipmentData?.shipmentValue;
    if (!shipmentError && value !== null && value !== undefined && value !== shipmentValue) {
      dispatch(SET_SHIPMENT_VALUE(value));
    }
  });

  const onOpenStepModal = (type: ModalTypesEnum) => {
    switch (type) {
      case ModalTypesEnum.SCHEDULE:
        setModalScheduleVisible(true);
        break;
      case ModalTypesEnum.PAYMENT_METHOD:
        setModalPaymentMethodVisible(true);
        break;
      case ModalTypesEnum.CONTACT:
        setModalFallbackContactVisible(true);
        break;
      case ModalTypesEnum.CLIENT_DOC:
        setModalClientDocumentVisible(true);
        break;
      case ModalTypesEnum.VOUCHER:
        setModalVoucherVisible(true);
        break;
      default:
        break;
    }
  };

  const onChangeDeliveryTime = (deliveryResult: DeliveryTimeResult) => {
    setDeliveryTimeResult(deliveryResult);
    if (store?.congelamento_preco_ativo && !deliveryResult?.isExpressDelivery) {
      const outdateResult = checkItemsOutdateInPromo(
        Array.from(items.values()),
        deliveryResult.date
      );
      if (outdateResult?.isOutdated && outdateResult?.newSubtotal !== currentSubtotal) {
        setOutdatedPromoPricesInfo(outdateResult);
        setModalOutdatedPromoPriceVisible(true);
      }
    }
  };

  const onAddVoucher = (voucherDetails: VoucherDatails) => {
    setSelectedVoucher(voucherDetails);
    const { valor_desconto, tipo_desconto, condominio } = voucherDetails.voucher;
    if (condominio?.dias_promocao?.length) {
      setDeliveryTimeResult(null);
      setModalScheduleVisible(true);
    }
    switch (tipo_desconto) {
      case VoucherDiscountTypesEnum.DISCOUNT:
        setVoucherOrderDiscount(valor_desconto);
        break;
      case VoucherDiscountTypesEnum.SHIPMENT_DISCOUNT:
        if (valor_desconto > currentShipmentValue) {
          setCurrenShipmentValue(0);
        } else {
          setCurrenShipmentValue(currentShipmentValue - valor_desconto);
        }
        break;
      case VoucherDiscountTypesEnum.SHIPMENT_FREEE:
        setCurrenShipmentValue(0);
        break;
      default:
        break;
    }
    setModalVoucherVisible(false);
  };

  const onRemoveVoucher = () => {
    setCurrenShipmentValue(shipmentValue);
    setVoucherOrderDiscount(0);
    setSelectedVoucher(null);
    setModalVoucherVisible(false);
  };

  const voucherDetails = useMemo<OrderVoucherDetails | null>(() => {
    if (!selectedVoucher) return null;
    return {
      cupom: selectedVoucher.voucher,
      frete_inicial: selectedVoucher.initialShipmentValue,
      valor_total_inicial: selectedVoucher.initialTotal,
    };
  }, [selectedVoucher]);

  const onSubmitOrder = () => {
    const requiredSteps = [deliveryTimeResult, selectedPaymentMethod, selectedFallbackContact];
    if (shipmentValue === null || shipmentValue === undefined) {
      setModalShipmentErrorVisible(true);
      return null;
    }
    if (requiredSteps.includes(null)) return null;
    let requestDate = undefined;
    if (!deliveryTimeResult.isExpressDelivery && deliveryTimeResult.date) {
      const scheduleDate = deliveryTimeResult.date;
      requestDate = scheduleDate.toISOString();
    }
    mutateNewOrder({
      valor_total: total,
      agendado: !deliveryTimeResult.isExpressDelivery,
      dataAgendamento: requestDate,
      cliente: clientId,
      detalhes_pagamento: {
        frete: currentShipmentValue,
        subtotal: currentSubtotal,
        cpf: selectedClientDocument,
        dinheiro_pago_cliente: selectedPaymentMethod?.clientMoney,
        troco: selectedPaymentMethod?.clientChange,
        valor_desconto_cupom: selectedVoucher ? selectedVoucher.voucher.valor_desconto : undefined,
        valor_taxa_servico: serviceTaxValue ?? undefined,
      },
      detalhes_desconto: selectedVoucher
        ? {
            cupom: selectedVoucher.voucher._id,
            frete_inicial: selectedVoucher.initialShipmentValue,
            valor_total_inicial: selectedVoucher.initialTotal,
          }
        : undefined,
      endereco_cliente: address?._id,
      estabelecimento: store?._id,
      itens: Array.from(items.values()).map((item) => ({
        item: item?.item._id,
        quantidade: item.quantidade,
        peso: item.peso,
        preco_unitario: item.preco_unitario,
        preco_total: item.preco_total,
      })),
      metodo_pagamento: {
        descricao: selectedPaymentMethod?.description,
        metodo: selectedPaymentMethod.method,
        tipo: selectedPaymentMethod.type,
      },
      opcao_falta_estoque: {
        contato_reserva: {
          nome: selectedFallbackContact.name,
          telefone: selectedFallbackContact.phone,
        },
        observacao: selectedFallbackContact?.description,
      },
      produtos_nao_encontrados: itemsNotFound,
    });
  };

  const Screen = useMemo(() => (isWebMode ? CheckoutWeb : CheckoutMobile), [isWebMode]);

  return (
    <>
      <Screen
        isLoading={isLoading}
        isDisabled={[deliveryTimeResult, selectedPaymentMethod, selectedFallbackContact].includes(
          null
        )}
        items={Array.from(items.values())}
        shipmentValue={currentShipmentValue}
        store={store}
        subtotal={currentSubtotal}
        total={total}
        serviceTaxValue={serviceTaxValue}
        clientDocument={selectedClientDocument}
        fallbackContact={selectedFallbackContact}
        paymentMethod={selectedPaymentMethod}
        deliveryTime={deliveryTimeResult}
        voucherDetails={voucherDetails}
        onOpenModal={onOpenStepModal}
        onPressBack={() => goBack()}
        onFinishCheckout={() => onSubmitOrder()}
      />
      <AlertModal
        isOpen={isFocused() && modalShipmentErrorVisible}
        title="Endereço indisponível"
        message="Este estabelecimento não atende o endereço selecionado, por escolha outro endereço."
        onClose={() => {
          setModalShipmentErrorVisible((prev) => !prev);
          goBack();
        }}
        onPressPrimaryButton={() => {
          setModalShipmentErrorVisible((prev) => !prev);
          navigate("addressStack");
        }}
      />
      <ScheduleModal
        isOpen={modalScheduleVisible}
        storeId={store?._id}
        currentValue={deliveryTimeResult}
        restrictedDays={selectedVoucher?.restrictedDays}
        onClose={() => setModalScheduleVisible(false)}
        onSubmit={onChangeDeliveryTime}
      />
      <OutdatedPromoPricesModal
        isOpen={modalOutdatedPromoPriceVisible}
        currentValue={currentSubtotal}
        newSubtotal={outdatedPromoPricesInfo?.newSubtotal}
        onClose={() => {
          setModalScheduleVisible(true);
          setModalOutdatedPromoPriceVisible(false);
          setOutdatedPromoPricesInfo(null);
        }}
        onSubmit={(newSubtotal) => {
          setCurrenSubtotal(newSubtotal);
          setModalOutdatedPromoPriceVisible(false);
          setSelectedPaymentMethod(null);
        }}
      />
      <PaymentMethodModal
        isOpen={modalPaymentMethodVisible}
        store={store}
        orderTotal={total}
        currentValue={selectedPaymentMethod}
        onClose={() => setModalPaymentMethodVisible(false)}
        onSubmit={(method) => setSelectedPaymentMethod(method)}
      />
      <ClientDocumentModal
        isOpen={modalClientDocumentVisible}
        currentValue={selectedClientDocument}
        onClose={() => setModalClientDocumentVisible(false)}
        onSubmit={(doc) => setSelectedClientDocument(doc)}
      />
      <VoucherModal
        isOpen={modalVoucherVisible}
        currentValue={selectedVoucher}
        clientId={clientId}
        shipmentValue={shipmentValue}
        storeId={store?._id}
        subtotal={subtotal}
        onClose={() => setModalVoucherVisible(false)}
        onSubmit={(voucher) => onAddVoucher(voucher)}
        onRemove={onRemoveVoucher}
      />
      <FallbackContactModal
        isOpen={modalFallbackContactVisible}
        currentValue={selectedFallbackContact}
        onClose={() => setModalFallbackContactVisible(false)}
        onSubmit={(contact) => setSelectedFallbackContact(contact)}
      />
      <OrderCompletedModal
        isOpen={modalOrderSuccessVisible}
        onClose={() => setModalOrderSuccessVisible(false)}
      />
    </>
  );
};

export default Checkout;
